import { QueryKey } from "@/api";
import { Api } from "@/api/Api";
import { networkCaches } from "@/cache/GlobalCache";
import { FileType } from "@/types/file";
import type { ModelFile as CustomGeoJSONFile } from "@mm/metx-workbench.meteomatics.com";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GeoJSON } from "geojson";
import { useMemo } from "react";

export type UploadGeoJsonResponse = CustomGeoJSONFile & {
  warning_codes?: string[];
  warnings?: string[];
};

function invalidateCustomGeojsonListCaches(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOM_GEOJSON_LIST] });
  networkCaches.generic_cache.invalidate([QueryKey.CUSTOM_GEOJSON_LIST]);
}

export const useGetCustomGeoJsonList = (shouldFetch = true) => {
  const { data, isLoading, error } = useQuery<CustomGeoJSONFile[], Error>(
    [QueryKey.CUSTOM_GEOJSON_LIST],
    () => Api.file.listFiles({ fileType: FileType.CUSTOM_GEOJSON }),
    {
      staleTime: Number.POSITIVE_INFINITY,
      enabled: shouldFetch,
    },
  );
  return { list: data, isLoading, error };
};

export const useGetCustomGeoJsonByUserFileId = (userFileId: number | null) => {
  const { data, isLoading, error } = useQuery<GeoJSON, Error>(
    [QueryKey.CUSTOM_GEOJSON_FILE, userFileId],
    // biome-ignore lint/style/noNonNullAssertion: not enabled if there is no id
    () => Api.file.getFile({ userFileId: userFileId! }),
    {
      enabled: userFileId !== null,
      staleTime: Number.POSITIVE_INFINITY,
    },
  );
  return { geojson: data, isLoading, error };
};

export const useGetCustomGEOJSONStorageInfo = () => {
  const { data, isLoading, error } = useQuery(
    [QueryKey.FILE_STORAGE_INFO],
    () => Api.file.getStorageInfo({ fileType: FileType.CUSTOM_GEOJSON }),
    {
      staleTime: Number.POSITIVE_INFINITY,
    },
  );
  return { data: data, isLoading, error };
};

export const useFindCustomGeoJsonById = (userFileId: number) => {
  const { list, isLoading, error } = useGetCustomGeoJsonList(true);

  const data = useMemo(() => list?.find((item) => item.user_file_id === userFileId), [list, userFileId]);

  return { data, isLoading, error };
};

const ensureGeoJsonFilename = (name: string): string => {
  if (/\.geojson$/i.test(name)) {
    return name;
  }
  if (/\.json$/i.test(name)) {
    return name.replace(/\.json$/i, ".geojson");
  }
  return `${name}.geojson`;
};

/**
 * Coerces a given Blob into a File with the appropriate MIME type for GeoJSON.
 *
 * Some environments (especially Windows) may provide uploaded files with a generic
 * `application/octet-stream` type. This helper only constructs a new File if the input
 * type is octet-stream (or empty), in which case it sets the type to `application/geo+json`
 * while preserving the original filename and lastModified (when the input is a File).
 * @see  https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/MIME_types#applicationoctet-stream
 *
 * @param {Blob} input - The raw file blob provided by the browser.
 * @param {string} name - The original file name, used to detect GeoJSON extensions.
 * @returns {File} A File instance with the corrected MIME type when applicable.
 */
const coerceGeoJsonFile = (input: Blob, name: string): File => {
  // Desired media type per RFC 7946.
  const desired = "application/geo+json";

  // If input has no type or is octet-stream, construct a new File with the same name/lastModified.
  const isOctet = !input.type || input.type === "application/octet-stream";
  if (isOctet) {
    const filename = input instanceof File ? input.name : name;
    const fixedName = ensureGeoJsonFilename(filename);
    const lastModified = input instanceof File ? input.lastModified : undefined;
    return new File([input], fixedName, { type: desired, lastModified });
  }

  // Otherwise, do not change anything—return the original File if possible.
  // (If the caller passed a Blob here, wrap it in a File so we can submit a named part.)
  return input instanceof File ? input : new File([input], name, { type: input.type });
};

export const useUploadGeoJson = () => {
  const queryClient = useQueryClient();
  return useMutation<UploadGeoJsonResponse, Error, { fileName: string; file: Blob }>({
    mutationFn: async ({ fileName, file }: { fileName: string; file: Blob }) => {
      const fixedFile =
        file instanceof File
          ? coerceGeoJsonFile(file, fileName)
          : coerceGeoJsonFile(new File([file], fileName), fileName);

      const csrf = await Api.security.getCsrfTokenApiV2SecurityCsrfTokenGet({
        credentials: "include",
      });
      const authToken = await Api.config?.accessToken?.("OAuth2", []);
      return (await Api.file.uploadFile(
        {
          fileType: FileType.CUSTOM_GEOJSON,
          label: fileName,
          file: fixedFile,
        },
        {
          credentials: "include",
          headers: {
            "X-CSRF-Token": csrf.csrf_token,
            Authorization: authToken || "",
          },
        },
      )) as UploadGeoJsonResponse;
    },
    onSuccess: () => {
      // TODO: cleanup / remove old keys
      queryClient.invalidateQueries({ queryKey: [QueryKey.CUSTOM_GEOJSON_SIZE] });
      invalidateCustomGeojsonListCaches(queryClient);
      queryClient.invalidateQueries({ queryKey: [QueryKey.FILE_STORAGE_INFO] });
    },
  });
};
export const useUpdateGeoJsonLabel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileId, newLabel }: { fileId: number; newLabel: string }) => {
      const csrf = await Api.security.getCsrfTokenApiV2SecurityCsrfTokenGet();
      const authToken = await Api.config?.accessToken?.("OAuth2", []);

      // UpdateFileLabelRequest
      return await Api.file.updateFileLabel(
        {
          fileId,
          newLabel,
        },
        {
          headers: {
            "X-CSRF-Token": csrf.csrf_token,
            Authorization: authToken || "",
          },
        },
      );
    },
    onSuccess: () => {
      invalidateCustomGeojsonListCaches(queryClient);
    },
  });
};

export const useDeleteGeoJsonFile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userFileId: number) => {
      const csrf = await Api.security.getCsrfTokenApiV2SecurityCsrfTokenGet();
      const authToken = await Api.config?.accessToken?.("OAuth2", []);

      return await Api.file.deleteFile(
        { userFileId },
        {
          headers: {
            "X-CSRF-Token": csrf.csrf_token,
            Authorization: authToken || "",
          },
        },
      );
    },
    onSuccess: () => {
      invalidateCustomGeojsonListCaches(queryClient);
      queryClient.invalidateQueries({ queryKey: [QueryKey.FILE_STORAGE_INFO] });
    },
  });
};
